import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  House,
  Leaf, 
  Sparkle, 
  Wrench, 
  Question, 
  Heart, 
  Headset,
  CaretDown,
  CaretRight
} from '@phosphor-icons/react';
import { PUBLIC_ROUTES } from '../../constants/routes';
import { getHelpArticleRoute } from '../../constants/routes';
import { isOnHelpSubdomain, getHelpRoute } from '../../utils/navigation';
import './HelpSidebar.css';

/**
 * HelpSidebar Component
 * Vertical navigation sidebar for Help Center pages
 * Includes icons, text, and arrow indicators
 * Expandable menu for "Get started"
 */
const HelpSidebar = () => {
  const location = useLocation();
  const isOnHelp = isOnHelpSubdomain();
  
  // Normalize pathname for comparison (handle both clean and full routes)
  const normalizePath = (path) => {
    if (isOnHelp) {
      return path.replace('/help', '') || '/';
    }
    return path;
  };
  
  const normalizedPathname = normalizePath(location.pathname);
  
  const [expandedItems, setExpandedItems] = useState(() => {
    // Auto-expand categories if we're on their pages
    const expanded = [];
    const gettingStartedPath = normalizePath(PUBLIC_ROUTES.HELP_GETTING_STARTED);
    const featuresPath = normalizePath(PUBLIC_ROUTES.HELP_FEATURES);
    const integrationsPath = normalizePath('/help/integrations');
    
    if (normalizedPathname.startsWith(gettingStartedPath) || 
        normalizedPathname === normalizePath(PUBLIC_ROUTES.HELP)) {
      expanded.push('get-started');
    }
    if (normalizedPathname.startsWith(featuresPath)) {
      expanded.push('features');
    }
    if (normalizedPathname.startsWith(integrationsPath)) {
      expanded.push('integrations');
    }
    return expanded;
  });

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const getStartedSubItems = [
    { label: 'Creating an Account', route: getHelpRoute(getHelpArticleRoute('getting-started', 'welcome')) },
    { label: 'Secure your Account', route: getHelpRoute(getHelpArticleRoute('getting-started', 'first-split')) },
    { label: 'Top up your account', route: getHelpRoute(getHelpArticleRoute('getting-started', 'top-up-account')) },
    { label: 'Create your first group', route: getHelpRoute(getHelpArticleRoute('getting-started', 'create-group')) },
    { label: 'Manage your group', route: getHelpRoute(getHelpArticleRoute('getting-started', 'manage-group')) },
    { label: 'Top up your group', route: getHelpRoute(getHelpArticleRoute('getting-started', 'top-up-group')) },
    { label: 'Withdraw from a group', route: getHelpRoute(getHelpArticleRoute('getting-started', 'withdraw-group')) },
  ];

  const featuresSubItems = [
    { label: 'Link Cards & Wallets', route: getHelpRoute(getHelpArticleRoute('features', 'link-cards-wallets')) },
    { label: 'Your Profile', route: getHelpRoute(getHelpArticleRoute('features', 'your-profile')) },
    { label: 'Badge Community', route: getHelpRoute(getHelpArticleRoute('features', 'badge-community')) },
    { label: 'Send (P2P)', route: getHelpRoute(getHelpArticleRoute('features', 'send-p2p')) },
    { label: 'Request (P2P)', route: getHelpRoute(getHelpArticleRoute('features', 'request-p2p')) },
    { label: 'Gas Fees', route: getHelpRoute(getHelpArticleRoute('features', 'gas-fees')) },
    { label: 'Rewards', route: getHelpRoute(getHelpArticleRoute('features', 'rewards')) },
    { label: 'Groups', route: getHelpRoute(getHelpArticleRoute('features', 'groups')) },
  ];

  const integrationsSubItems = [
    { label: 'Phantom Connect', route: getHelpRoute(getHelpArticleRoute('integrations', 'integration-1')) },
  ];

  const navItems = [
    { 
      id: 'get-started',
      path: getHelpRoute(PUBLIC_ROUTES.HELP_GETTING_STARTED), 
      label: 'Get started', 
      icon: Leaf,
      exact: true,
      subItems: getStartedSubItems.map(item => ({
        ...item,
        route: getHelpRoute(item.route)
      }))
    },
    { 
      id: 'features',
      path: getHelpRoute(PUBLIC_ROUTES.HELP_FEATURES), 
      label: 'Features', 
      icon: Sparkle,
      exact: true,
      subItems: featuresSubItems.map(item => ({
        ...item,
        route: getHelpRoute(item.route)
      }))
    },
    { 
      id: 'integrations',
      path: getHelpRoute('/help/integrations'), 
      label: 'Integrations', 
      icon: Wrench,
      exact: true,
      subItems: integrationsSubItems.map(item => ({
        ...item,
        route: getHelpRoute(item.route)
      }))
    },
    { 
      id: 'faq',
      path: getHelpRoute('/help/faq'), 
      label: 'FAQ', 
      icon: Question 
    },
    { 
      id: 'community',
      path: getHelpRoute('/help/community'), 
      label: 'Community', 
      icon: Heart 
    },
    { 
      id: 'support',
      path: getHelpRoute('/help/support'), 
      label: 'Need Help?', 
      icon: Headset 
    },
  ];

  const isActive = (path, exact = false) => {
    const normalizedItemPath = normalizePath(path);
    if (exact) {
      return normalizedPathname === normalizedItemPath;
    }
    return normalizedPathname.startsWith(normalizedItemPath);
  };

  const isSubItemActive = (subItemRoute) => {
    return normalizedPathname === normalizePath(subItemRoute);
  };

  const homeActive = normalizedPathname === normalizePath(PUBLIC_ROUTES.HELP);

  return (
    <nav className="help-sidebar">
      <ul className="help-sidebar-list">
        <li>
          <div className="help-sidebar-item-wrapper">
            <div className={`help-sidebar-link-wrapper ${homeActive ? 'active' : ''}`}>
              <Link
                to={getHelpRoute(PUBLIC_ROUTES.HELP)}
                className={`help-sidebar-link ${homeActive ? 'active' : ''}`}
              >
                <House weight={homeActive ? 'fill' : 'regular'} size={20} className="help-sidebar-icon" />
                <span className="help-sidebar-text">Home</span>
              </Link>
              <CaretRight weight="regular" size={16} className="help-sidebar-arrow" />
            </div>
          </div>
        </li>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path, item.exact);
          const isExpanded = expandedItems.includes(item.id);
          const hasSubItems = item.subItems && item.subItems.length > 0;

          return (
            <li key={item.id}>
              <div className="help-sidebar-item-wrapper">
                <div className={`help-sidebar-link-wrapper ${active ? 'active' : ''}`}>
                  <Link
                    to={item.path}
                    className={`help-sidebar-link ${active ? 'active' : ''}`}
                  >
                    <Icon weight={active ? 'fill' : 'regular'} size={20} className="help-sidebar-icon" />
                    <span className="help-sidebar-text">{item.label}</span>
                  </Link>
                  {hasSubItems && (
                    <button
                      className="help-sidebar-expand-btn"
                      onClick={() => toggleExpand(item.id)}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? (
                        <CaretDown weight="regular" size={16} />
                      ) : (
                        <CaretRight weight="regular" size={16} />
                      )}
                    </button>
                  )}
                  {!hasSubItems && (
                    <CaretRight weight="regular" size={16} className="help-sidebar-arrow" />
                  )}
                </div>
                {hasSubItems && isExpanded && (
                  <ul className="help-sidebar-sublist">
                    {item.subItems.map((subItem) => {
                      const subActive = isSubItemActive(subItem.route);
                      return (
                        <li key={subItem.route}>
                          <Link
                            to={subItem.route}
                            className={`help-sidebar-sublink ${subActive ? 'active' : ''}`}
                          >
                            {subItem.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default HelpSidebar;
